"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * This hook debounces a passed in value by a given delay in milliseconds. After the delay, the
 * latest value will be returned.
 *
 * This is useful for delaying a state update or expensive computation when there may be a burst
 * of state updates in a short period of time (e.g. user inputs, event listener events).
 *
 * @param value The value to be debounced
 * @param delay The delay time in milliseconds
 * @returns The debounced value
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * A hook that debounces a callback function. The callback will only be called after
 * the specified delay has passed since the last time it was triggered.
 *
 * @param callback The function to debounce
 * @param delay The delay in milliseconds
 * @returns A debounced version of the callback
 */
export function useDebouncedCallback<TArgs extends readonly unknown[]>(
  callback: (...args: TArgs) => void,
  delay: number,
): { debouncedCallback: (...args: TArgs) => void; isDebouncing: boolean } {
  const [isDebouncing, setIsDebouncing] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedCallback = useCallback(
    (...args: TArgs) => {
      setIsDebouncing(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
        setIsDebouncing(false);
      }, delay);
    },
    [callback, delay],
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { debouncedCallback, isDebouncing };
}
